import React from "react";
import Calendar from 'react-calendar';

function capitalizeFirstLetter(str) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

const CustomCalendarPicker = ({
  from = null,
  to = null,
  calendarStartDate = new Date(),
  setCalendarStartDate = () => {},
  maxDate = new Date(),
  selectRange = true,
  handleDateChange = () => {},
  useHoveredDate = from && !to && selectRange
}) => {
  const [hoveredDate, setHoveredDate] = React.useState(null);
  const onChange = (val) => {
    const [start, end] = val;
    from = start;
    to = end;
    setHoveredDate(null);
    handleDateChange({ from: start || null, to: end || null });
  };
  
  return (
    <Calendar
    value={from ? [from, to || null] : null}
    allowPartialRange={true}
    onChange={onChange}
    goToRangeStartOnSelect
    selectRange={selectRange}
    activeStartDate={calendarStartDate}
    onActiveStartDateChange={({ activeStartDate }) => setCalendarStartDate(activeStartDate)}
    showFixedNumberOfWeeks
      next2Label={null}
      prev2Label={null}
      nextLabel=""
      prevLabel=""
      navigationLabel={({ label }) => <span className="body-m-semibold">{label}</span>}
      formatShortWeekday={(locale, date) => capitalizeFirstLetter(date.toLocaleDateString(locale, { weekday: "short" }))}
      tileContent={({ date, view }) => {
        if (view !== 'month') return null;
        const dateStr = date.toDateString();
        const fromStr = from?.toDateString();
        const effectiveTo = from && to === null && hoveredDate ? hoveredDate : to;
        
        const hasRange = from && effectiveTo;
        
        const [rangeStart, rangeEnd] = hasRange
        ? from < effectiveTo
        ? [from, effectiveTo]
            : [effectiveTo, from]
          : [null, null];

        const isStart = from && dateStr === from.toDateString();
        const isTempEnd = useHoveredDate && hoveredDate && dateStr === hoveredDate.toDateString();
        const isFinalEnd = !useHoveredDate && to && dateStr === to.toDateString();
        const isEnd = effectiveTo && dateStr === effectiveTo.toDateString();
        const isInRange = hasRange && ((date >= rangeStart && date <= rangeEnd) || isStart || isTempEnd || isFinalEnd);
        const isEffectiveStart = rangeStart && dateStr === rangeStart.toDateString();
        const isEffectiveEnd = rangeEnd && dateStr === rangeEnd.toDateString();
        const isToday = dateStr === new Date().toDateString();
        const isCurrentMonth = date.getMonth() === calendarStartDate.getMonth() && date.getFullYear() === calendarStartDate.getFullYear();

        const disableHoverBackground = isStart || isToday || isTempEnd || isFinalEnd;

        const rangeColor = 'var(--color-action-primary-surface)';
        const startEndColor = 'var(--color-action-primary)';
        const transparent = 'transparent';

        const tileBg = (isToday && !isStart && !isFinalEnd) ? 'var(--red-500)' : (isStart || isFinalEnd ? startEndColor : transparent);
        const tileRadius = isToday && !isStart && !isEnd ? 'var(--radius-circle)' : (isStart || isEnd ? 'var(--radius-xs)' : 0);
        const tileFontColor = isStart || isFinalEnd || isToday ? 'var(--color-text-inverse-primary)' : isCurrentMonth ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)';
        const tileBorder = isTempEnd ? 'var(--border-width-s) solid var(--color-action-primary)' : 'none';
        const wrapperBgColor = isInRange ? rangeColor : transparent;

        const tileBorderRadius = {
          borderTopLeftRadius: isEffectiveStart ? 'var(--radius-xs)' : '0px',
          borderBottomLeftRadius: isEffectiveStart ? 'var(--radius-xs)' : '0px',
          borderTopRightRadius: isEffectiveEnd ? 'var(--radius-xs)' : '0px',
          borderBottomRightRadius: isEffectiveEnd ? 'var(--radius-xs)' : '0px',
        };

        let leftSpacerColor = transparent;
        let rightSpacerColor = transparent;

        if (hasRange && isInRange) {
          if (isEffectiveStart || date.getDay() === 1) leftSpacerColor = transparent;
          else leftSpacerColor = rangeColor;
          if (isEffectiveEnd || date.getDay() === 0) rightSpacerColor = transparent;
          else rightSpacerColor = rangeColor;
      }
        return (
          <div
            className="calendar-tile-wrapper"
            onMouseEnter={() => useHoveredDate && (selectRange && from && !to) && setHoveredDate(date)}
            onMouseLeave={() => useHoveredDate && setHoveredDate(null)}
          >
            <div className="calendar-tile-grid">
              <div style={{ backgroundColor: leftSpacerColor }} />
              <div className="calendar-tile-center">
                <div className="calendar-tile-range-bg" style={{ backgroundColor: wrapperBgColor, ...tileBorderRadius }} />
                <div
                  className={`calendar-bubble tile ${disableHoverBackground ? 'no-hover-bg' : ''} ${isInRange ? 'in-range' : ''} ${isToday || isStart || isFinalEnd ? 'no-hovering-bg' : ''}`}
                  style={{ backgroundColor: tileBg, borderRadius: tileRadius, color: tileFontColor, outline: tileBorder }}
                >
                  {date.getDate()}
                </div>
              </div>
              <div style={{ backgroundColor: rightSpacerColor }} />
            </div>
          </div>
        );
      }}
    />
  );
};

export default CustomCalendarPicker;
